import { useLoyalty } from '../../../contexts/LoyaltyContext';

interface LoyaltyFriendStandingsProps {
  title?: string;
  maxFriends?: number;
}

function AvatarSmall({ firstName, lastName, avatarUrl }: { firstName: string | null; lastName: string | null; avatarUrl: string | null }) {
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '?';

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={`${firstName ?? ''} ${lastName ?? ''}`}
        className="w-8 h-8 rounded-full object-cover border border-[var(--color-surface-border)]"
      />
    );
  }

  return (
    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold bg-[var(--color-primary)] text-white">
      {initials}
    </div>
  );
}

function LoginCta() {
  return (
    <div className="loyalty-card p-6 text-center">
      <div className="text-3xl mb-2 opacity-50">&#128101;</div>
      <h2 className="loyalty-heading text-base mb-1 text-[var(--color-text)]">
        Friend Standings
      </h2>
      <p className="text-[var(--color-text-muted)] text-xs mb-3">
        Sign in to see how your friends are doing.
      </p>
      <a
        href="/login"
        className="inline-block px-4 py-1.5 rounded-lg bg-[var(--color-primary)] text-white font-semibold text-xs hover:opacity-90 transition-opacity"
      >
        Sign In
      </a>
    </div>
  );
}

export function LoyaltyFriendStandingsBlock({ props }: { props: LoyaltyFriendStandingsProps }) {
  const { data, loading } = useLoyalty();
  const maxFriends = props.maxFriends ?? 5;
  const title = props.title ?? 'Friend Standings';

  if (loading) {
    return (
      <div className="loyalty-card p-4 animate-pulse">
        <div className="h-5 w-32 bg-[var(--color-surface-border)] rounded mb-4" />
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-8 bg-[var(--color-surface-border)] rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) return <LoginCta />;

  const friends = data.friendStandings.slice(0, maxFriends);

  return (
    <div className="loyalty-card p-4">
      <h3 className="loyalty-heading text-sm text-[var(--color-text)] mb-3">
        {title}
      </h3>

      {friends.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)] text-center py-4">
          No friend standings available yet.
        </p>
      ) : (
        <div className="space-y-2">
          {friends.map((friend) => {
            const isCurrentUser = friend.customerId === data.profile.firstName; // highlight by context
            return (
              <div
                key={friend.customerId}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                  isCurrentUser ? 'bg-[var(--color-primary)]/10' : ''
                }`}
              >
                <span className="text-[var(--color-text-muted)] w-5 text-right text-xs font-bold">
                  #{friend.rank}
                </span>
                <AvatarSmall
                  firstName={friend.firstName}
                  lastName={friend.lastName}
                  avatarUrl={friend.avatarUrl}
                />
                <span className="flex-1 truncate text-[var(--color-text)] text-xs font-medium">
                  {friend.firstName ?? ''} {friend.lastName ?? ''}
                </span>
                <span className="font-bold text-[var(--color-primary)] text-xs whitespace-nowrap">
                  {friend.points.toLocaleString()} <span className="opacity-60">PTS</span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
